import {FC} from 'react';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {Box, Chip, IconButton, Link, Tooltip, Typography} from '@mui/material';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  formatOnboardingStatus,
  formatOnboardingType,
  OnboardingStatus,
  OnboardingType,
  useGetUserOnboardingStatuses,
  useResetUserOnboardingStatuses,
  UserOnboardingStatus,
} from '@entities/onboarding';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  MrTable,
  openConfirmation,
  TableRowActionsContainer,
} from '@shared/components';
import {entityTableCommonProps} from '@shared/constants';

const columns: MRT_ColumnDef<UserOnboardingStatus>[] = [
  {
    accessorKey: 'type',
    header: 'Onboarding',
    size: 200,
    enableSorting: false,
    Cell: ({row}) => formatOnboardingType(row.original.type),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 140,
    enableSorting: false,
    Cell: ({row}) => {
      const isCompleted = row.original.status === OnboardingStatus.completed;
      return (
        <Chip
          label={formatOnboardingStatus(row.original.status)}
          color={isCompleted ? 'success' : 'default'}
          size="small"
        />
      );
    },
  },
  {
    accessorKey: 'config',
    header: 'Config',
    enableSorting: false,
    Cell: ({row}) =>
      row.original.config ? (
        <Link
          href={row.original.config}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.original.config}
        </Link>
      ) : (
        '—'
      ),
  },
];

export const UserOnboardingsSection: FC<{userId: string}> = ({userId}) => {
  const {enqueueSnackbar} = useSnackbar();

  const {
    data: onboardings = [],
    isPending,
    error,
  } = useGetUserOnboardingStatuses(userId);

  const {mutate: resetOnboardings, isPending: isResetting} =
    useResetUserOnboardingStatuses();

  const completedTypes = onboardings
    .filter(item => item.status === OnboardingStatus.completed)
    .map(item => item.type);

  const runReset = (types: OnboardingType[], successMessage: string) => {
    resetOnboardings(
      {userId, types},
      {
        onSuccess: () => {
          enqueueSnackbar(successMessage, {variant: 'success'});
        },
        onError: err => {
          const msg = isAxiosError(err) ? getErrorMessageFromApiError(err) : '';
          enqueueSnackbar(`Could not reset onboarding. ${msg}`, {
            variant: 'error',
          });
        },
      },
    );
  };

  const handleResetOne = async (onboarding: UserOnboardingStatus) => {
    try {
      await openConfirmation({
        title: 'Reset onboarding',
        content: `Reset the "${formatOnboardingType(onboarding.type)}" onboarding for this user?`,
        okButtonText: 'Reset',
      });
    } catch {
      return;
    }

    runReset([onboarding.type], 'Onboarding reset successfully');
  };

  const handleResetAll = async () => {
    try {
      await openConfirmation({
        title: 'Reset all onboardings',
        content: `Reset all ${completedTypes.length} completed onboardings for this user?`,
        okButtonText: 'Reset all',
      });
    } catch {
      return;
    }

    runReset(completedTypes, 'Onboardings reset successfully');
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1.5}
      >
        <Typography variant="h3">Onboardings</Typography>
        <Tooltip
          title={
            completedTypes.length > 0 ? '' : 'No completed onboardings to reset'
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={isResetting || completedTypes.length === 0}
              onClick={() => void handleResetAll()}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <MrTable
        columns={columns}
        data={onboardings}
        error={error}
        state={{isLoading: isPending}}
        renderRowActions={({row}) => (
          <TableRowActionsContainer>
            <Tooltip
              title={
                row.original.status === OnboardingStatus.completed
                  ? 'Reset'
                  : 'Nothing to reset'
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={
                    isResetting ||
                    row.original.status !== OnboardingStatus.completed
                  }
                  onClick={() => void handleResetOne(row.original)}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </TableRowActionsContainer>
        )}
        {...entityTableCommonProps}
      />
    </Box>
  );
};
